import * as React from 'react';
import * as _ from 'underscore';

import {
  DropdownButton,
  DropdownButtonType
} from '@tableau/widgets';
import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { L10n } from '../../../ts/L10n/L10n';
import { ExplorableItemDefinitions, MixedContentItemDefinition } from '../../../ts/models/ContentDefinitions';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { SpaceType } from '../../state/SpaceInfo';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerListMenuPanelOwnProps } from '../Components/ExplorerListMenuPanel';
import { ShowWorkbookAsViewsValue } from './ConnectedFilterByTypeMenu';
import { ConnectedFilterByTypePanel } from './ConnectedFilterbyTypePanel';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';

export interface FilterByTypeDropdownProps {
  readonly filterSelected: (selectedContentTypes: ReadonlyArray<ExplorableItemType>) => void;
  readonly contentFilter: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
  readonly spaceType: SpaceType;
  readonly menuPanelClass?: React.ComponentClass<ExplorerListMenuPanelOwnProps>; // remove with removal of contentTypeMenuUpdate FF
  readonly showWorkbooksAsViews?: boolean;
  readonly toggleShowWorkbooksAsViews?: (showWorkbooksAsViews: boolean) => void;
  readonly contentItemDefinitions: MixedContentItemDefinition[];
}

export class FilterByTypeDropdown extends React.PureComponent<FilterByTypeDropdownProps> {
  private dropdownButtonRef: React.RefObject<DropdownButton> = React.createRef();

  public componentDidMount() {
    // if navigating between pages in the same space (e.g. one project to another) need to clear the previous counts,
    // otherwise menu will show previous counts from another menu until contentCountsRequest completes
    if(FeatureFlags.contentTypeMenuUpdate) {
      this.props.clearContentCounts(this.props.spaceType);
    }
  }

  public close = (): void => {
    if (this.dropdownButtonRef.current) {
      this.dropdownButtonRef.current.toggle(false);
    }
  }

  public render(): JSX.Element {
    if (FeatureFlags.contentTypeMenuUpdate) {
      return (
        <DropdownButton
          aria-labelledby={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel}
          aria-controls={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-popup`}
          aria-haspopup={'dialog'}
          label={''}
          ref={this.dropdownButtonRef}
          closeOnPopupBlur={true}
          type={DropdownButtonType.Line}
          testId={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-Button`}
          popupContent={
            <div id={`${ExplorerToolbarTestIds.FilterByTypeDropdown}-popup`} role={'dialog'}>
              <ConnectedFilterByTypePanel
                defaultContentTypeFilterValues={this.props.defaultContentTypeFilterValues}
                toggleShowWorkbooksAsViews={this.props.toggleShowWorkbooksAsViews}
                filterSelected={this.props.filterSelected}
                closePanel={this.close}
                contentItemDefinitions={this.props.contentItemDefinitions}
              />
            </div>
          }
          style={{ minWidth: 130 }}
        >
          {this.renderButtonContentUpdated()}
        </DropdownButton>
      );
    } else {
      return (
        <ExplorerDropdown
          testId={ExplorerToolbarTestIds.FilterByTypeDropdown}
          renderButtonContent={this.renderButtonContent}
          renderPanel={this.renderPanel}
          panelTitle={Messages.filterByTypeDropdown_title() }
          buttonStyleOverrides={{height: 'auto'}}
        />
      );
    }
  }

  // delete with removal of contentTypeMenuUpdate FF
  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return (
      <this.props.menuPanelClass
        onSelect={(value: string) => {
          this.handleSelection(value);
        }}
        anchorEl={props.anchorEl}
        testId={props.testId}
        onCloseRequest={props.onCloseRequest}
        id={`${props.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`}
        role={'menu'}
      />
    );
  }

  // delete with removal of contentTypeMenuUpdate FF
  private renderButtonContent = (): JSX.Element => {
    return <span>{this.getButtonText()}</span>;
  }

  // delete with removal of contentTypeMenuUpdate FF
  private getButtonText = (): string => {
    const selectedCount = this.props.contentFilter.length;
    const maxSelectedCount = this.props.defaultContentTypeFilterValues.length;

    if (selectedCount === maxSelectedCount) {
      return Messages.filterByTypeDropdown_showAll();
    } else if(selectedCount === 1) {
      if (this.props.contentFilter[0] === 'dataconnection') {
        return ExplorableItemDefinitions['datasource'].pluralName();
      } else {
        // Check if itemDef exists in case the content type in the store does not yet match: TFSID 977220
        const itemDef = ExplorableItemDefinitions[this.props.contentFilter[0]];
        if (!_.isUndefined(itemDef)) {
          return ExplorableItemDefinitions[this.props.contentFilter[0]].pluralName();
        }
      }
    } else if (selectedCount === 0) {
      return Messages.filterByTypeDropdown_showNone();
    }

    return Messages.filterByTypeDropdown_showSome();
  }

  private renderButtonContentUpdated = (): JSX.Element => {
    return <span>{this.getButtonTextUpdated()}</span>;
  }

  private getButtonTextUpdated = (): string => {
    const selectedCount = this.props.contentFilter.length;
    const maxSelectedCount = this.props.defaultContentTypeFilterValues.length;

    if(selectedCount < 1) {
      return; // should never reach this state
    }

    if (selectedCount === maxSelectedCount) {
      return Messages.all();
    } else if(selectedCount === 1) {
      return this.getButtonTextForSingleContentType();
    } else {
      return Messages.filterByTypeDropdown_someSelected({
        FIRST_CONTENT_TYPE: ExplorableItemDefinitions[this.props.contentFilter[0]].pluralName(),
        ADDITIONAL_COUNT_LOC: L10n.localizedCount(selectedCount - 1)
      });
    }
  }

  private getButtonTextForSingleContentType() {
    if (this.props.contentFilter[0] === 'dataconnection') {
      return ExplorableItemDefinitions['datasource'].pluralName();
    }

    // TFSID 977220: Check if itemDef exists in case the content type in the store does not yet match,
    // only necessary to check when contentFilter.length === 1
    const itemDef = ExplorableItemDefinitions[this.props.contentFilter[0]];
    if (!_.isUndefined(itemDef)) {
      return ExplorableItemDefinitions[this.props.contentFilter[0]].pluralName();
    } else {
      return Messages.all();
    }
  }

  // delete with removal of contentTypeMenuUpdate FF
  private handleSelection = (value: string) => {
    const selectedContentTypes: ExplorableItemType[] = [...this.props.contentFilter];

    if (value === ShowWorkbookAsViewsValue) {
      if (!this.props.toggleShowWorkbooksAsViews) {
        return;
      }
      const showWorkbooksAsViews = !this.props.showWorkbooksAsViews;
      this.props.toggleShowWorkbooksAsViews(showWorkbooksAsViews);
      swapWorkbookAndViewContentTypes(selectedContentTypes, showWorkbooksAsViews);
    } else if (selectedContentTypes.length === 1 && selectedContentTypes[0] === 'dataconnection' && value === 'datasource') {
      // Special case when data connection is being shown but user goes to unselect data source
      // Remove data connection from selected content types.
      selectedContentTypes.splice(0, 1);
    } else {
      toggleSelectedContentType(selectedContentTypes, value as ExploreContentApi_ContentType);
    }

    // if selected content type contains dataconnection swap it with datasource
    const indexOfDataconnection = selectedContentTypes.indexOf('dataconnection');
    if (indexOfDataconnection > -1) {
      selectedContentTypes[indexOfDataconnection] = 'datasource';
    }

    this.props.filterSelected(selectedContentTypes);
  }
}

/**
 * Modifies the ContentType array provided, swapping workbook for view, if showWorkbooksAsViews is true;
 * Otherwise, it swaps view for workbook. If neither is present in the array, it is not modified at all.
 */
export function swapWorkbookAndViewContentTypes(selectedContentTypes: ExplorableItemType[], showWorkbooksAsViews: boolean) {
  const swapFrom = showWorkbooksAsViews ? 'workbook' : 'view';
  const swapWith = showWorkbooksAsViews ? 'view' : 'workbook';
  const indexOfSwap = selectedContentTypes.indexOf(swapFrom);
  if (indexOfSwap > -1) {
    selectedContentTypes[indexOfSwap] = swapWith;
  }
}

/**
 * Modifies the ContentType array provided, adding or removing the specified contentTypeToToggle.
 */
export function toggleSelectedContentType(selectedContentTypes: ExplorableItemType[], contentTypeToToggle: ExploreContentApi_ContentType) {
    const selectedIndex = selectedContentTypes.indexOf(contentTypeToToggle);

    if (selectedIndex > -1) {
      selectedContentTypes.splice(selectedIndex, 1);
    } else {
      selectedContentTypes.push(contentTypeToToggle);
    }
}
